import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  CircularProgress, 
  ToggleButton, 
  ToggleButtonGroup, 
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Cached,
  Analytics,
  ShowChart,
  GridView,
  Notes,
  Functions,
  Person,
  Checklist,
  FlightTakeoff,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { formatTZDate } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import MetricCard from 'components/MetricCard';
import GenericDialog from 'components/GenericDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';
import DrillDownTable from "./DrillDownTable";
import TopFlightsCard from './TopFlightsCard';
import TopUsersCard from './TopUsersCard';


export function countLogsByDay(
  logs,
  fieldName = "logForDay",
  chartType,
  legend = "Logs",
  legendAvg = "LogsAvg"
) {
  if (!Array.isArray(logs) || logs.length === 0) return [];

  const obj = logs.reduce((acc, log) => {
    const key = log[fieldName];
    if (!key) return acc;

    if (!acc[key]) acc[key] = 0;
    acc[key] += 1;

    return acc;
  }, {});

  if (!Object.keys(obj).length) return [];

  // sort theo ngày
  const arrLogs = Object.entries(obj)
    .map(([key, value]) => ({
      x: key,
      y: value,
    }))
    .sort((a, b) => new Date(a.x) - new Date(b.x));

  // average
  const total = arrLogs.reduce((sum, cur) => sum + cur.y, 0);
  const avg = parseFloat((total / arrLogs.length).toFixed(1));

  const arrLogsAvg = arrLogs.map(({ x }) => ({
    x,
    y: avg,
  }));

  const barLineChartData = [
    { id: legend, data: arrLogs },
    { id: legendAvg, data: arrLogsAvg },
  ];

  switch (chartType) {
    case "bar":
    case "column":
      return arrLogs;
    case "barline":
    default:
      return barLineChartData;
  }
}

const FlightScheduleChanges = () => {
  useAuthCheck();
  const { startDate, endDate } = useDateRangeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [flightLogs, setFlightLogs] = useState([]);
  const [view, setView] = useState('stats');
  const [drillMode, setDrillMode] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [logDialog, setLogDialog] = useState({
    open: false,
    title: "",
    data: [],
    loading: false,
  });


  const handleFlightScheduleChanges = useCallback(async () => {
    if (!startDate || !endDate) return;

    try {
      setLoading(true);
      setError(null);

      const url = '/flightschedulechanges';
      const payload = { startDate, endDate };

      const res = await api.post(url, payload);
      setFlightLogs(res.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    handleFlightScheduleChanges();
  }, [handleFlightScheduleChanges]);

  const handleUserClick = useCallback((userKey) => {
    setDrillMode("user");
    setSelectedKey(userKey);
    setLogDialog({
      open: true,
      title: `User: ${userKey}`,
      loading: false,
    });
  }, []);

  const handleFlightClick = useCallback((flight) => {
    setDrillMode("flight");
    setSelectedKey(flight.key); 
    setLogDialog({
      open: true,
      title: `Flight: ${flight.label}`,
      loading: false,
    });
  }, []);

  const handleCloseLogDialog = () => {
    setLogDialog({ open: false, title: "", loading: false });
    setDrillMode(null);
    setSelectedKey(null);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      ...(isMobile ? { width: 60 } : { flex: 1 }),
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "logForDay",
      headerName: "Log Day",
      ...(isMobile ? { width: 90 } : { flex: 1.5 }), 
      valueFormatter: (value) => value ? formatTZDate(value) : "",
    },
    {
      field: "flightDesc",
      headerName: "FlightNo",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }), 
    },
    {
      field: "sector",
      headerName: "Sector",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }), 
    },
    {
      field: "acType",
      headerName: "AC Type",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }), 
    },
    {
      field: "flightReg",
      headerName: "Reg",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }), 
    },
    {
      field: "std",
      headerName: "Std",
      ...(isMobile ? { width: 60 } : { flex: 1 }), 
    },

    {
      field: "sta",
      headerName: "Sta",
      ...(isMobile ? { width: 60 } : { flex: 1 }), 
    },
    {
      field: "atd",
      headerName: "Atd",
      ...(isMobile ? { width: 60 } : { flex: 1 }), 
    },

    {
      field: "ata",
      headerName: "Ata",
      ...(isMobile ? { width: 60 } : { flex: 1 }), 
    },
    {
      field: "logDetails",
      headerName: "LogDetails",
      ...(isMobile ? { width: 300 } : { flex: 5 }), 
      sortable: false,
      renderCell: (params) => {
        const logs = params.row.logDetails || [];

        return (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
            }}
          >
            {logs.map((log, index) => (
              <Box
                key={index}
                display="flex"
                flexDirection="column"
                gap={0.25}
                sx={{
                  mb: 0.75,
                  pb: 0.75,
                  borderBottom:
                    index !== logs.length - 1
                      ? "1px dashed #ddd"
                      : "none",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ color: "text.secondary" }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: 60,
                    }}
                  >
                    {log.logDayTime}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    User: {log.logChangedBy}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {log.logDescription}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    }
  ], [isMobile]);

  const hiddenColumns = useMemo(() => {
    return {
      _id: false,
      no: false,
    };
  }, []); 

  const nonMobileHiddenColumns = useMemo(() => {
    return {
      _id: false,
    };
  }, []);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);

  const stats = useMemo(() => {
    if (!flightLogs.length) return [];

    let totalLogs = 0;
    const userSet = new Set();
    const flightMap = new Map();

    flightLogs.forEach((f) => {
      const key = `${f.flightDate}|${f.flightNo}|${f.dep}`;
      flightMap.set(key, (flightMap.get(key) || 0) + f.logDetails.length);

      f.logDetails.forEach((d) => {
        totalLogs += 1;
        if (d.logChangedBy) userSet.add(d.logChangedBy);
      });
    });

    const userCount = userSet.size;
    const flightCount = flightMap.size;
    const maxFlightLogs = Math.max(...flightMap.values());

    return [
      {
        title: "Total Logs",
        subtitle: "All actions",
        value: totalLogs?.toLocaleString('en-US'),
        icon: <Functions/>,
        bgColor: alpha(theme.palette.primary.main, 0.6),
      },
      {
        title: "Users",
        subtitle: "Active users",
        value: userCount?.toLocaleString('en-US'),
        icon: <Person/>,
        bgColor: alpha(theme.palette.info.main, 0.6),
      },
      {
        title: "Flights",
        subtitle: "Affected flights",
        value: flightCount?.toLocaleString('en-US'),
        icon: <FlightTakeoff/>,
        bgColor: alpha(theme.palette.success.main, 0.6),
      },
      {
        title: "Logs / User",
        subtitle: "Avg actions",
        value: userCount ? Number((totalLogs / userCount).toFixed(0))?.toLocaleString('en-US') : 0,
        icon: <Checklist/>,
        bgColor: alpha(theme.palette.warning.main, 0.6),
      },
      {
        title: "Logs / Flight",
        subtitle: "Avg per flight",
        value: flightCount ? Number((totalLogs / flightCount).toFixed(0))?.toLocaleString('en-US') : 0,
        icon: <ShowChart/>,
        bgColor: alpha(theme.palette.secondary.main, 0.6),
      },
      {
        title: "Max Logs / Flight",
        subtitle: "Peak flight",
        value: maxFlightLogs?.toLocaleString('en-US') || 0,
        icon: <Notes/>,
        bgColor: alpha(theme.palette.error.main, 0.6),
      },
    ];
  }, [theme.palette, flightLogs]);

  const topFlights = useMemo(() => {
    if (!flightLogs.length) return [];

    const map = new Map();
    const total = flightLogs.length;

    flightLogs.forEach((log) => {
      const key = `${formatTZDate(log.logForDay)}|${log.flightNo}|${log.dep}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: `${log.flightNo} ${log.sector} ${formatTZDate(log.logForDay)}`,
          count: 0,
        });
      }
      map.get(key).count += log.changesCount;
    });

    return [...map.values()]
      .map((f) => ({
        ...f,
        count: f.count?.toLocaleString('en-US'),
        percent: Math.round((f.count / total) * 1000) / 10,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  }, [flightLogs]);

  const topUsers = useMemo(() => {
    if (!flightLogs.length) return [];

    const map = new Map();
    let total = 0;

    flightLogs.forEach((f) => {
      f.logDetails.forEach((d) => {
        if (!d.logChangedBy) return;
        total += 1;
        map.set(d.logChangedBy, (map.get(d.logChangedBy) || 0) + 1);
      });
    });

    return [...map.entries()]
      .map(([user, count]) => ({
        key: user,
        label: user,
        count: count?.toLocaleString('en-US'),
        percent: Math.round((count / total) * 1000) / 10, // 1 decimal
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 25);
  }, [flightLogs]);
  
  const flightsByUser = useMemo(() => {
    if (drillMode !== "user" || !selectedKey) return [];

    const map = new Map();
    flightLogs.forEach((f) => {
      const key = `${formatTZDate(f.logForDay)}|${f.flightNo}|${f.dep}`;

      f.logDetails.forEach((d) => {
        if (d.logChangedBy !== selectedKey) return;

        if (!map.has(key)) {
          map.set(key, {
            key,
            label: `${f.flightNo} ${f.sector} ${formatTZDate(f.logForDay)}`,
            count: 0,
          });
        }

        map.get(key).count += 1;
      });
    });

    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [flightLogs, drillMode, selectedKey]);
  
  const usersByFlight = useMemo(() => {
    if (drillMode !== "flight" || !selectedKey) return [];

    const map = new Map();

    flightLogs.forEach((f) => {
      const key = `${formatTZDate(f.logForDay)}|${f.flightNo}|${f.dep}`;
      if (key !== selectedKey) return;

      f.logDetails.forEach((d) => {
        if (!d.logChangedBy) return;

        if (!map.has(d.logChangedBy)) {
          map.set(d.logChangedBy, {
            key: d.logChangedBy,
            label: d.logChangedBy, 
            count: 0,
          });
        }

        map.get(d.logChangedBy).count += 1;
      });
    });

    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [flightLogs, drillMode, selectedKey]);

  const logsChartData = useMemo(() => {
    return countLogsByDay(
      flightLogs,
      "logForDay",
      "barline",
      "Logs",
      "Avg Logs"
    );
  }, [flightLogs]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Flight Logs" 
          subtitle={`${loading ? 'Loading...' : 'List of Changes'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <ToggleButtonGroup
              aria-label="ViewToggle"
              exclusive
              value={view}
              onChange={(e, value) => value && setView(value)}
            >
              {[
                { value: "stats", label: "stats", icon: <Analytics /> },
                { value: "chart", label: "chart", icon: <ShowChart /> },
                { value: "grid", label: "grid", icon: <GridView /> },
              ].map(({ value, label, icon }) => (
                <ToggleButton
                  size="small"
                  key={value}
                  aria-label={label}
                  value={value}
                >
                  {icon}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
         
            <Tooltip title="Refresh" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    handleFlightScheduleChanges();
                  }}
                >
                  <Cached />
                </StyledButton>
              </span>
            </Tooltip>  
          </Box>
        )}
      </FlexBetween>                      
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box
            ref={scrollRef} 
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {view === 'stats' && (      
              <Box 
                display="flex" 
                flexDirection="column"              
                gap={2}
              >
                <Box     
                  display="grid"
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))"         
                  gridAutoRows={100}
                  gap={2}           
                  sx={{
                    "& > div": {
                      gridColumn: isDesktop
                        ? "span 2"
                        : isTablet
                        ? "span 4"
                        : "span 6",
                    },
                  }}
                > 
                  {stats?.map((stat, index) => (
                    <MetricCard
                      key={index}
                      title={stat.title}                 
                      subtitle={stat.subtitle}      
                      value={stat.value}      
                      icon={stat.icon}      
                      bgColor={stat.bgColor}      
                    />
                  ))}        
                </Box>

                <Box         
                  display="grid"
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))"       
                  gridAutoRows={86}       
                  gap={2}           
                >               
                  <Box 
                    gridColumn={
                      isDesktop ? "span 6"
                      : isTablet ? "span 12"
                      : "span 12"
                    }
                    gridRow={ "span 4" }
                    overflow="auto"
                  >
                    <TopFlightsCard 
                      data={topFlights} 
                      onFlightClick={handleFlightClick} 
                    />
                  </Box>
                  
                  <Box
                    gridColumn={
                      isDesktop ? "span 6"
                      : isTablet ? "span 12"
                      : "span 12"
                    }
                    gridRow="span 4"
                  >
                    <TopUsersCard
                      data={topUsers}
                      onUserClick={handleUserClick}
                    />
                  </Box>
                </Box>
              </Box>    
            )}
            {view === 'chart' && (           
              <Box
                height="100%"
                sx={styles}    
              >                             
                <SortBarMultiLinesChart
                  data={logsChartData}
                  title="Logs by Day"
                  bottomLegend="Date"
                  leftLegend="Count"
                  digit={0}
                  barTranslateXOffSet={120} 
                  lineTranslateXOffSet={-15}
                />
              </Box> 
            )}
            {view === 'grid' && (       
              <DataGridComponent                
                rows={flightLogs}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}
      
      <GenericDialog
        open={logDialog.open}
        onClose={handleCloseLogDialog}
        title={logDialog.title}
        loading={logDialog.loading}
      >
        {drillMode === "user" && (
          <DrillDownTable
            data={flightsByUser?.map((f, i) => ({
              id: i,
              label: f.label,
              count: f.count,
            }))}
            type="flight"
          />
        )}

        {drillMode === "flight" && (
          <DrillDownTable
            data={usersByFlight?.map((u, i) => ({
              id: i,
              label: u.label,
              count: u.count,
            }))}
            type="user"
          />
        )}
      </GenericDialog>
    </Box>
  );
};

export default FlightScheduleChanges;
